import { Locales, ReminderBasedOn } from '@griply/types';
import {
  getDefaultTaskReminder,
  getTaskReminderSettingOptions,
  getTaskReminderSettingUpdate,
  getTaskReminderSettingValue,
} from '@griply/utils';
import Typography from '@mui/material/Typography';
import { useUpdateUser } from 'features/user';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info } from 'shared/assets/icons';
import { Alert, Colors as AlertColors } from 'shared/components/ui/alert';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import {
  CustomFormSelect,
  Size as SelectSize,
} from 'shared/components/ui/custom-form-select';
import { Divider } from 'shared/components/ui/divider';
import { Toggle } from 'shared/components/ui/toggle';
import { Positions, Tooltip } from 'shared/components/ui/tooltip';
import { useCachedPushNotificationTokenQuery } from 'shared/hooks/queries/use-cached-push-notification-token-query';
import { useCreatePushNotificationMutation } from 'shared/hooks/queries/use-create-push-notification-mutation';
import { useDeletePushNotificationMutation } from 'shared/hooks/queries/use-delete-push-notification-mutation';
import { useNotificationPermission } from 'shared/hooks/use-notification-permission';
import { useUser } from 'shared/hooks/use-user';
import { getErrorReporter } from 'shared/services/error-tracking';
import { PushRegistrationError } from 'shared/types/electron';
import { Notifications } from 'shared/types/user-settings';
import { getIsElectronTag } from 'shared/utils/get-is-electron-tag';

import { getPushErrorTranslationKey } from './get-push-error-translation-key';
import * as Styled from './settings-form.style';

const InfoTooltip: React.FC<{ title: string }> = ({ title }) => {
  const ref = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <Styled.InfoIconWrapper ref={ref}>
        <Info />
      </Styled.InfoIconWrapper>
      <Tooltip
        label={title}
        referenceElement={ref}
        position={Positions.Top}
        isVisible={undefined}
      />
    </>
  );
};

export const NotificationsToggles: React.FC = () => {
  const { t } = useTranslation();
  const user = useUser();
  const language = user?.settings?.language;
  const notifications = user?.settings?.notifications;

  const plannedReminderValue = getTaskReminderSettingValue(
    getDefaultTaskReminder(notifications, ReminderBasedOn.Start),
  );
  const allDayReminderValue = getTaskReminderSettingValue(
    getDefaultTaskReminder(notifications, ReminderBasedOn.DayStart),
  );

  const plannedReminderOptions = React.useMemo(
    () => getTaskReminderSettingOptions(ReminderBasedOn.Start, t),
    [t],
  );
  const allDayReminderOptions = React.useMemo(
    () => getTaskReminderSettingOptions(ReminderBasedOn.DayStart, t),
    [t],
  );

  const taskRemindersEnabled = !!notifications?.taskReminder?.desktop;

  const [permissions, requestNotificationPermission] =
    useNotificationPermission();
  const { data: pushNotificationToken } = useCachedPushNotificationTokenQuery(
    user?.uid,
  );

  const {
    mutateAsync: createPushToken,
    isError: createFailed,
    error: createError,
  } = useCreatePushNotificationMutation();
  const { mutateAsync: deletePushToken, isError: deleteFailed } =
    useDeletePushNotificationMutation();
  const { submit: updateUser } = useUpdateUser();

  const onTogglePush = async () => {
    if (!user?.uid) {
      return;
    }

    const isEnabling = !pushNotificationToken;

    try {
      if (isEnabling) {
        await createPushToken({
          userId: user.uid,
          locale: language ?? Locales.EnUS,
        });
        return;
      }

      await deletePushToken({ token: pushNotificationToken });
    } catch (error) {
      getErrorReporter().captureException(error, {
        tags: {
          source: 'notifications-toggle',
          action: isEnabling ? 'enable' : 'disable',
          is_electron: getIsElectronTag(),
          ...(error instanceof PushRegistrationError && { code: error.code }),
        },
      });
    }
  };

  const onChangeNotification = ({
    target,
  }: React.ChangeEvent<HTMLInputElement>) => {
    const categoryKey = target.name as keyof Notifications;
    const categoryOption = target.value as keyof Notifications['taskReminder'];
    const category = notifications?.[categoryKey] ?? {};
    const oldValue = category?.[categoryOption] ?? false;

    updateUser(
      {
        notifications: {
          ...(notifications ?? {}),
          [categoryKey]: {
            ...category,
            [categoryOption]: !oldValue,
          },
        },
      },
      user,
    );
  };

  const onChangeReminder =
    (basedOn: ReminderBasedOn) => (value: string | undefined) => {
      updateUser(
        {
          notifications: getTaskReminderSettingUpdate(
            notifications,
            basedOn,
            value,
          ),
        },
        user,
      );
    };

  const onChangePlannedReminder = onChangeReminder(ReminderBasedOn.Start);
  const onChangeAllDayReminder = onChangeReminder(ReminderBasedOn.DayStart);

  return (
    <Styled.Section>
      <Styled.SettingItem $autoWidthField>
        <Styled.LabelContainer>
          <Typography variant="body2" component="span">
            {t(
              window.electron
                ? 'forms.user-settings.sections.notifications.receive-in-app'
                : 'forms.user-settings.sections.notifications.receive-in-browser',
            )}
          </Typography>
        </Styled.LabelContainer>
        <Toggle
          checked={!!pushNotificationToken}
          onChange={onTogglePush}
          value="desktop-push"
        />
      </Styled.SettingItem>

      {(createFailed || deleteFailed) && (
        <Alert startIcon={Info} color={AlertColors.Error}>
          <Typography variant="overline" component="span">
            {t(getPushErrorTranslationKey(createError))}
          </Typography>
        </Alert>
      )}

      {permissions === 'prompt' && (
        <Alert
          startIcon={Info}
          color={AlertColors.Warning}
          action={
            <Button
              variant={Variants.Outlined}
              color={Colors.Warning}
              size={Sizes.Small}
              onClick={requestNotificationPermission}
            >
              {t(
                'forms.user-settings.sections.notifications.warning.undecided.activate-button.label',
              )}
            </Button>
          }
        >
          <Typography variant="overline" component="span">
            {t(
              'forms.user-settings.sections.notifications.warning.undecided.message.label',
            )}
          </Typography>
        </Alert>
      )}

      {permissions === 'denied' && (
        <Alert startIcon={Info} color={AlertColors.Warning}>
          <Typography variant="overline" component="span">
            {t(
              'forms.user-settings.sections.notifications.warning.denied.message.label',
            )}
          </Typography>
        </Alert>
      )}

      {!!pushNotificationToken && (
        <>
          <Divider />

          <Typography variant="h6" component="h3">
            {t(
              'forms.user-settings.sections.notifications.task-reminders.title',
            )}
          </Typography>

          <Styled.SettingItem $autoWidthField>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.task-reminder.label',
                )}
              </Typography>
            </Styled.LabelContainer>
            <Toggle
              name="taskReminder"
              value="desktop"
              checked={taskRemindersEnabled}
              onChange={onChangeNotification}
            />
          </Styled.SettingItem>

          {taskRemindersEnabled && (
            <>
              <Styled.SettingItem>
                <Styled.LabelContainer>
                  <Typography variant="body2" component="span">
                    {t('reminders.settings.planned-task.label')}
                  </Typography>
                  <InfoTooltip
                    title={t('reminders.settings.planned-task.description')}
                  />
                </Styled.LabelContainer>
                <CustomFormSelect
                  options={plannedReminderOptions}
                  value={plannedReminderValue}
                  onChange={onChangePlannedReminder}
                  size={SelectSize.ExtraSmall}
                />
              </Styled.SettingItem>

              <Styled.SettingItem>
                <Styled.LabelContainer>
                  <Typography variant="body2" component="span">
                    {t('reminders.settings.all-day-task.label')}
                  </Typography>
                  <InfoTooltip
                    title={t('reminders.settings.all-day-task.description')}
                  />
                </Styled.LabelContainer>
                <CustomFormSelect
                  options={allDayReminderOptions}
                  value={allDayReminderValue}
                  onChange={onChangeAllDayReminder}
                  size={SelectSize.ExtraSmall}
                />
              </Styled.SettingItem>
            </>
          )}

          <Divider />

          <Typography variant="h6" component="h3">
            {t('reminders.settings.sections.calendar-events')}
          </Typography>

          <Styled.SettingItem $autoWidthField>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t('reminders.settings.external-calendar-events.label')}
              </Typography>
              <InfoTooltip
                title={t(
                  'reminders.settings.external-calendar-events.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="externalCalendarEvent"
              value="desktop"
              checked={!!notifications?.externalCalendarEvent?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SettingItem>

          <Divider />

          <Typography variant="h6" component="h3">
            {t('reminders.settings.sections.other')}
          </Typography>
          <Styled.SettingItem $autoWidthField>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.task-summary.label',
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  'forms.user-settings.sections.notifications.fields.task-summary.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="taskSummary"
              value="desktop"
              checked={!!notifications?.taskSummary?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SettingItem>
          <Styled.SettingItem $autoWidthField>
            <Styled.LabelContainer>
              <Typography variant="body2" component="span">
                {t(
                  'forms.user-settings.sections.notifications.fields.weekly-review.label',
                )}
              </Typography>
              <InfoTooltip
                title={t(
                  'forms.user-settings.sections.notifications.fields.weekly-review.description',
                )}
              />
            </Styled.LabelContainer>
            <Toggle
              name="weeklyReview"
              value="desktop"
              checked={!!notifications?.weeklyReview?.desktop}
              onChange={onChangeNotification}
            />
          </Styled.SettingItem>
        </>
      )}
    </Styled.Section>
  );
};
